/**
 * BulkAndLoopSimulator — Supabase-native bulk + AI loop simulator.
 * Creates synthetic Supabase contacts, runs agent.execute() with integration payload.
 * No Telerivet.
 */

import { useState } from "react"
import Markdown from "react-markdown"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Loader2, Play, Trash2, Upload, Plus, Sparkles, Square, AlertTriangle, ChevronRight, ChevronDown, Bot, User, FlaskConical } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { AllAIModels } from "@/lib/agents/modellist"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useBulkTest, useAIChatLoop } from "../hooks"
import { generateFakeName } from "../utils"
import { openaiService } from "../services"
import type { TestCase } from "../types"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

const DEFAULT_SYSTEM_PROMPT = "You are simulating a real person who needs help. Ask realistic questions and respond naturally. Keep your messages short (1-2 sentences)."

const SCENARIOS_PER_REQUEST = 3

function InlineMarkdownTitle({ value }: { value: string }) {
  return (
    <Markdown
      components={{
        p: ({ children }) => <>{children}</>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children }) => <code className="bg-muted px-0.5 rounded text-[10px]">{children}</code>,
      }}
      allowedElements={["p", "strong", "em", "code"]}
      unwrapDisallowed
    >
      {value}
    </Markdown>
  )
}

function extractJsonArray(text: string): any[] | null {
  let cleaned = text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim()
  try {
    const parsed = JSON.parse(cleaned)
    if (Array.isArray(parsed)) return parsed
    for (const v of Object.values(parsed)) {
      if (Array.isArray(v)) return v
    }
  } catch {}
  const arrayMatch = cleaned.match(/\[[\s\S]*\]/)
  if (arrayMatch) {
    try {
      const parsed = JSON.parse(arrayMatch[0])
      if (Array.isArray(parsed)) return parsed
    } catch {}
  }
  return null
}

interface AgentOption {
  id: number | string
  title: string
}

interface BulkAndLoopSimulatorProps {
  agentId?: string
  teamId?: string
  agents?: AgentOption[]
  loadingAgents?: boolean
}

export function BulkAndLoopSimulator({ agentId: initialAgentId, teamId, agents = [], loadingAgents }: BulkAndLoopSimulatorProps = {}) {
  const [selectedAgentId, setSelectedAgentId] = useState<string>(initialAgentId || "")
  const agentId = selectedAgentId || initialAgentId

  const [aiLoopEnabled, setAILoopEnabled] = useState(false)
  const [rounds, setRounds] = useState(3)
  const [userCount, setUserCount] = useState(10)
  const [csvInput, setCsvInput] = useState("")
  const [model, setModel] = useState("openai/gpt-4.1-mini")
  const [generatePrompt, setGeneratePrompt] = useState("")
  const [generating, setGenerating] = useState(false)
  const [importCsvOpen, setImportCsvOpen] = useState(false)
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set())

  const { toast } = useToast()
  const {
    testCases,
    running: bulkRunning,
    progress,
    addTestCase,
    removeTestCase,
    updateTestCase,
    runAllTests,
    importFromCSV,
    clear,
  } = useBulkTest()
  const { threads, running: loopRunning, startLoop, stopLoop, clear: clearThreads } = useAIChatLoop()

  const running = bulkRunning || loopRunning

  const handleGenerateUsers = () => {
    for (let i = 0; i < userCount; i++) {
      addTestCase({
        label: generateFakeName(),
        message: `Test message ${i + 1}`,
        ...(aiLoopEnabled ? { systemPrompt: DEFAULT_SYSTEM_PROMPT } : {}),
      })
    }
    toast({ title: "Users generated", description: `${userCount} test cases added` })
  }

  const handleImportCSV = (): boolean => {
    try {
      importFromCSV(csvInput)
      setCsvInput("")
      toast({ title: "Import successful", description: "CSV imported" })
      return true
    } catch {
      toast({ title: "Import failed", description: "Invalid CSV format", variant: "destructive" })
      return false
    }
  }

  const handleImportCSVAndClose = () => {
    if (handleImportCSV()) setImportCsvOpen(false)
  }

  const handleAIGenerate = async () => {
    if (!generatePrompt.trim() || !teamId) return
    setGenerating(true)
    let addedCount = 0
    try {
      await openaiService.initialize(teamId)
      while (addedCount < userCount) {
        const remaining = userCount - addedCount
        const batchSize = Math.min(SCENARIOS_PER_REQUEST, remaining)
        const prompt = `Generate exactly ${batchSize} test conversation scenario(s) for a chatbot.
Context: "${generatePrompt}"
Return a JSON object with a "scenarios" key containing an array of exactly ${batchSize} item(s). Each scenario has:
- "systemPrompt": instructions for an LLM simulating a real user
- "firstMessage": the opening message the simulated user sends
Example: {"scenarios": [{"systemPrompt": "You are a worried parent...", "firstMessage": "Hola, necesito ayuda"}]}
Make scenarios diverse and realistic.`
        const result = await openaiService.generateNextMessage(
          "You generate JSON only. Always return valid JSON with a scenarios array.",
          [{ role: "user", content: prompt }],
          model
        )
        const generated = extractJsonArray(result)
        if (!generated?.length) break
        // Only take up to `remaining` items in case the LLM returns more than asked
        const toAdd = generated.slice(0, remaining)
        toAdd.forEach((g: { systemPrompt?: string; firstMessage?: string }) => {
          addTestCase({
            label: generateFakeName(),
            message: g.firstMessage || "Hello",
            systemPrompt: g.systemPrompt || DEFAULT_SYSTEM_PROMPT,
          })
          addedCount++
        })
      }
      toast({ title: "AI Generate", description: `Added ${addedCount} scenarios` })
    } catch {
      toast({ title: "AI Generate failed", description: "Try a shorter prompt or fewer count", variant: "destructive" })
    } finally {
      setGenerating(false)
    }
  }

  const handleRun = async () => {
    if (!agentId || !teamId) {
      toast({ title: "Missing configuration", description: "Select an agent first", variant: "destructive" })
      return
    }
    if (testCases.length === 0) {
      toast({ title: "No test cases", description: "Add or import rows first", variant: "destructive" })
      return
    }
    if (aiLoopEnabled) {
      try {
      await startLoop({
        conversations: testCases.map((tc) => ({
          testCaseId: tc.id,
          label: tc.label,
          firstMessage: tc.message,
          systemPrompt: tc.systemPrompt || DEFAULT_SYSTEM_PROMPT,
        })),
          systemPrompt: "",
          model,
          maxTurns: rounds,
          agentId,
          teamId,
        })
        toast({ title: "AI Loop started", description: `${testCases.length} conversations` })
      } catch (err) {
        toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to start loop", variant: "destructive" })
      }
    } else {
      try {
        await runAllTests(undefined, agentId, teamId)
        toast({ title: "Bulk test complete", description: `Processed ${testCases.length} test cases` })
      } catch {
        toast({ title: "Error", description: "Failed to run bulk test", variant: "destructive" })
      }
    }
  }

  const toggleThread = (id: string) => {
    setExpandedThreads((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const activeCount = threads.filter((t) => t.status === "active").length
  const completedCount = threads.filter((t) => t.status === "completed").length
  const errorCount = threads.filter((t) => t.status === "error").length
  const escalatedCount = threads.filter((t) => t.status === "escalated").length

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-4">
          {/* Agent selector */}
          {agents.length > 0 && (
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap text-muted-foreground">Agent</Label>
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId} disabled={loadingAgents}>
                <SelectTrigger className="h-8 w-[220px] text-xs">
                  <SelectValue placeholder={loadingAgents ? "Loading…" : "Select agent"} />
                </SelectTrigger>
                <SelectContent>
                  {agents.map(a => (
                    <SelectItem key={a.id} value={String(a.id)} className="text-xs">
                      <InlineMarkdownTitle value={a.title} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Top row: AI Chat Loop, USERS, Generate, AI Generate, Import CSV */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={aiLoopEnabled}
                onChange={(e) => setAILoopEnabled(e.target.checked)}
                className="rounded border-input"
              />
              <span className="text-sm font-medium">AI Chat Loop</span>
            </label>
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">USERS:</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={userCount}
                onChange={(e) => setUserCount(parseInt(e.target.value) || 10)}
                className="w-20 h-8 text-xs"
              />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={handleGenerateUsers}>
              <Plus className="h-3 w-3 mr-1" />
              Generate
            </Button>
            <Button size="sm" variant="secondary" className="h-8 text-xs" onClick={handleAIGenerate} disabled={generating || !teamId}>
              {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              + AI Generate
            </Button>
            <Button size="sm" className="h-8 text-xs" onClick={() => setImportCsvOpen(true)}>
              <Upload className="h-3 w-3 mr-1" />
              Import CSV
            </Button>
          </div>

          {/* Import CSV modal */}
          <Dialog open={importCsvOpen} onOpenChange={setImportCsvOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Import CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-2">
                <Textarea
                  placeholder="Paste CSV here (phone_number,message) or just messages (one per line)"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  rows={5}
                  className="text-xs font-mono"
                />
                <p className="text-[10px] text-muted-foreground">
                  Format: phone_number,message (or just message for auto-generated numbers). Optional third column: system prompt for AI loop.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" size="sm" onClick={() => setImportCsvOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleImportCSVAndClose}>
                  <Upload className="h-3 w-3 mr-1" />
                  Import
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Model when AI Loop — vertically stacked */}
          {aiLoopEnabled && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">LLM Model</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger className="h-8 w-[180px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AllAIModels.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-xs">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">AI Generate context (optional)</Label>
                <Input
                  placeholder="e.g. Spanish-speaking migrants, housing and medical..."
                  value={generatePrompt}
                  onChange={(e) => setGeneratePrompt(e.target.value)}
                  className="h-7 text-xs mt-0.5"
                />
              </div>
            </div>
          )}

          {/* Progress (bulk) */}
          {bulkRunning && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {/* Run / Stop */}
          <div className="flex gap-2 items-center flex-wrap">
            {aiLoopEnabled && (
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Rounds:</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={rounds}
                  onChange={(e) => setRounds(parseInt(e.target.value) || 3)}
                  className="w-14 h-9 text-xs"
                />
              </div>
            )}
            <Button onClick={handleRun} disabled={running || testCases.length === 0} size="lg" className="flex-1 min-w-0">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              {running
                ? aiLoopEnabled
                  ? `Running (${activeCount} active)...`
                  : "Running..."
                : aiLoopEnabled
                  ? `Start Loop (${testCases.length})`
                  : `Run All (${testCases.length})`}
            </Button>
            {loopRunning && (
              <Button onClick={stopLoop} variant="destructive" size="lg">
                <Square className="h-4 w-4 mr-1" />
                Stop
              </Button>
            )}
            {testCases.length > 0 && !running && (
              <Button variant="outline" size="lg" onClick={() => { clear(); clearThreads() }}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Table */}
          {testCases.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="w-8">#</TableHead>
                    <TableHead>NAME</TableHead>
                    <TableHead>MESSAGE</TableHead>
                    {aiLoopEnabled && <TableHead>SYSTEM PROMPT</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testCases.map((tc, idx) => (
                    <TableRow key={tc.id} className="text-xs">
                      <TableCell className="font-mono text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell>
                        <Input
                          value={tc.label}
                          onChange={(e) => updateTestCase(tc.id, { label: e.target.value })}
                          className="h-7 text-xs"
                          disabled={running}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={tc.message}
                          onChange={(e) => updateTestCase(tc.id, { message: e.target.value })}
                          placeholder="Type message..."
                          className="h-7 text-xs"
                          disabled={running}
                        />
                      </TableCell>
                      {aiLoopEnabled && (
                        <TableCell>
                          <Input
                            value={tc.systemPrompt || ""}
                            onChange={(e) => updateTestCase(tc.id, { systemPrompt: e.target.value })}
                            placeholder="You are a user seeking help..."
                            className="h-7 text-xs"
                            disabled={running}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {aiLoopEnabled && threads.length > 0 ? (
                          (() => {
                            const thread = threads.find((t) => t.testCaseId === tc.id)
                              || threads.find((t) => t.label === tc.label)
                            if (!thread) return <Badge variant="outline">pending</Badge>
                            return (
                              <Badge
                                variant={
                                  thread.status === "completed" ? "default" : thread.status === "error" || thread.status === "escalated" ? "destructive" : "secondary"
                                }
                                className={thread.status === "evaluating" ? "text-[10px] bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-100" : "text-[10px]"}
                              >
                                {(thread.status === "active") && <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />}
                                {thread.status === "evaluating" && <FlaskConical className="h-2.5 w-2.5 mr-0.5 animate-pulse" />}
                                {thread.status === "escalated" && <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />}
                                {thread.status}
                              </Badge>
                            )
                          })()
                        ) : (
                          <Badge
                            variant={
                              tc.status === "sent" ? "default" : tc.status === "error" ? "destructive" : tc.status === "sending" ? "secondary" : "outline"
                            }
                            className="text-[10px]"
                          >
                            {tc.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeTestCase(tc.id)} disabled={running}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Thread results (AI Loop) */}
          {aiLoopEnabled && threads.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <div className="flex gap-2 flex-wrap">
                {completedCount > 0 && <Badge variant="default" className="text-xs">{completedCount} completed</Badge>}
                {escalatedCount > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    {escalatedCount} escalated
                  </Badge>
                )}
                {errorCount > 0 && <Badge variant="destructive" className="text-xs">{errorCount} errors</Badge>}
                {activeCount > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    {activeCount} active
                  </Badge>
                )}
              </div>
              {threads.map((thread) => {
                const isExpanded = expandedThreads.has(thread.id)
                const lastTurn = thread.turns[thread.turns.length - 1]
                return (
                  <div key={thread.id} className="border rounded-lg overflow-hidden">
                    <div
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/50 text-xs"
                      onClick={() => toggleThread(thread.id)}
                    >
                      {isExpanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                      <span className="font-medium w-32 shrink-0 truncate">{thread.label}</span>
                      <div className="flex-1 bg-secondary rounded-full h-1.5 min-w-0">
                        <div
                          className={`h-1.5 rounded-full transition-all ${
                            thread.status === "escalated" || thread.status === "error" ? "bg-red-500" : thread.status === "completed" ? "bg-green-500" : "bg-blue-500"
                          }`}
                          style={{ width: `${Math.min(100, (thread.currentTurn / rounds) * 100)}%` }}
                        />
                      </div>
                      <span className="text-muted-foreground shrink-0 w-8 text-right tabular-nums">{thread.currentTurn}/{rounds}</span>
                      <Badge
                        variant={thread.status === "completed" ? "default" : thread.status === "error" || thread.status === "escalated" ? "destructive" : "secondary"}
                        className={thread.status === "evaluating" ? "text-[9px] shrink-0 w-16 justify-center bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-100" : "text-[9px] shrink-0 w-16 justify-center"}
                      >
                        {thread.status === "active" && <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />}
                        {thread.status === "evaluating" && <FlaskConical className="h-2.5 w-2.5 mr-0.5 animate-pulse" />}
                        {thread.status === "escalated" && <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />}
                        {thread.status}
                      </Badge>
                      {lastTurn && !isExpanded && (
                        <span className="text-muted-foreground truncate max-w-[180px]">{lastTurn.content.substring(0, 50)}...</span>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="border-t px-3 py-2 max-h-[280px] overflow-y-auto space-y-1.5">
                        {thread.error && <div className="text-xs text-destructive">{thread.error}</div>}
                        {thread.turns.map((turn, idx) => (
                          <div key={idx} className={`flex ${turn.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div
                              className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs ${
                                turn.role === "user" ? "bg-blue-600 text-white" : "bg-muted"
                              }`}
                            >
                              <div className="flex items-center gap-1 mb-0.5 opacity-70">
                                {turn.role === "user" ? <User className="h-2 w-2" /> : <Bot className="h-2 w-2" />}
                                <span className="text-[9px]">{turn.role === "user" ? "User" : "Agent"}</span>
                              </div>
                              <p className="whitespace-pre-wrap">{turn.content}</p>
                            </div>
                          </div>
                        ))}
                        {thread.status === "active" && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
